import * as THREE from 'three';

/**
 * Gets the camera bounding box and frustum information
 * Uses Matterport Scene component to access the camera
 */
export async function getCameraBoundingBox(showcase: any) {
  try {
    // Method 1: Using ThreeJS camera frustum via Scene component (most reliable)
    if (showcase?.Scene) {
      try {
        // Register a temporary component to access the camera
        const componentType = 'camera-bbox-helper';

        // Check if already registered, if not register it
        try {
          await showcase.Scene.register(componentType, function() {
            return {
              outputs: {
                result: null
              },
              onInit: function() {
                const { camera } = this.context;

                if (!camera) {
                  this.outputs.result = null;
                  return;
                }

                // 1. Update camera matrices
                camera.updateMatrixWorld();
                camera.matrixWorldInverse.copy(camera.matrixWorld).invert();

                // 2. Create frustum using imported THREE library
                const frustum = new THREE.Frustum();
                const projScreenMatrix = new THREE.Matrix4();
                projScreenMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
                frustum.setFromProjectionMatrix(projScreenMatrix);

                // 3. Limit camera view distance to 10 meters
                const maxDistance = 10; // meters
                const cameraPos = camera.position;

                // 4. Get 8 corners of the frustum in NDC space and transform to world space
                const corners = [
                  new THREE.Vector3(-1, -1, -1), // near bottom-left
                  new THREE.Vector3(1, -1, -1),  // near bottom-right
                  new THREE.Vector3(1, 1, -1),   // near top-right
                  new THREE.Vector3(-1, 1, -1),  // near top-left
                  new THREE.Vector3(-1, -1, 1),  // far bottom-left
                  new THREE.Vector3(1, -1, 1),   // far bottom-right
                  new THREE.Vector3(1, 1, 1),    // far top-right
                  new THREE.Vector3(-1, 1, 1),   // far top-left
                ];

                const invProjectionMatrix = new THREE.Matrix4().copy(projScreenMatrix).invert();
                const boundingBox = new THREE.Box3();

                for (const corner of corners) {
                  // Transform corner from NDC to world coordinates
                  corner.applyMatrix4(invProjectionMatrix);

                  // Limit far corners to maxDistance from camera
                  const isFarCorner = corner.z > 0; // far plane corners have positive z in NDC
                  if (isFarCorner) {
                    const direction = new THREE.Vector3().subVectors(corner, cameraPos).normalize();
                    corner.copy(cameraPos).addScaledVector(direction, maxDistance);
                  }

                  // Expand bounding box to include this point
                  boundingBox.expandByPoint(corner);
                }

                // Get frustum planes for additional info
                const planes = frustum.planes.map((plane: any) => ({
                  normal: { x: plane.normal.x, y: plane.normal.y, z: plane.normal.z },
                  constant: plane.constant
                }));

                const result = {
                  boundingBox: {
                    min: { x: boundingBox.min.x, y: boundingBox.min.y, z: boundingBox.min.z },
                    max: { x: boundingBox.max.x, y: boundingBox.max.y, z: boundingBox.max.z }
                  },
                  dimensions: {
                    width: boundingBox.max.x - boundingBox.min.x,
                    height: boundingBox.max.y - boundingBox.min.y,
                    depth: boundingBox.max.z - boundingBox.min.z
                  },
                  cameraInfo: {
                    type: camera.type,
                    position: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
                    rotation: { x: camera.rotation.x, y: camera.rotation.y, z: camera.rotation.z },
                    fov: camera.fov,
                    aspect: camera.aspect,
                    near: camera.near,
                    far: camera.far
                  },
                  frustumPlanes: planes,
                  corners: corners.map((c: any) => ({ x: c.x, y: c.y, z: c.z }))
                };

                this.outputs.result = result;
              }
            };
          });
        } catch (regError: any) {
          // Component might already be registered, that's okay
          if (!regError.message?.includes('already registered')) {
            throw regError;
          }
        }

        // Create a node with the component
        const node = await showcase.Scene.createNode();
        const component = node.addComponent(componentType);
        node.start();

        // Get the result from the component
        const result = component.outputs.result;

        // Clean up the node
        node.stop();

        if (result) {
          return result;
        }
      } catch (err) {
        // Silent fallback
      }
    }

    // Fallback: Use Camera.getPose() if Scene method fails
    if (showcase?.Camera) {
      const pose = await showcase.Camera.getPose();
      return {
        cameraPosition: pose.position,
        cameraRotation: pose.rotation,
        mode: pose.mode
      };
    }

    return null;
  } catch (err) {
    return null;
  }
}

/**
 * Filters tags to find which ones are visible in the camera frustum
 */
export function getVisibleTags(tags: any[], cameraBoundingBox: any) {
  if (!tags || tags.length === 0) {
    return [];
  }

  if (!cameraBoundingBox?.frustumPlanes) {
    return [];
  }

  try {
    // Recreate the frustum from the bounding box data
    const frustum = new THREE.Frustum();
    const planes = cameraBoundingBox.frustumPlanes.map((p: any) =>
      new THREE.Plane(
        new THREE.Vector3(p.normal.x, p.normal.y, p.normal.z),
        p.constant
      )
    );
    frustum.planes = planes;

    // Filter tags that are inside the frustum
    const visibleTags = tags.filter(tag => {
      // Matterport tags use anchorPosition, custom tags use positionX/Y/Z
      let x: number, y: number, z: number;

      if (tag.anchorPosition) {
        // Matterport tag format
        x = tag.anchorPosition.x;
        y = tag.anchorPosition.y;
        z = tag.anchorPosition.z;
      } else if (typeof tag.positionX === 'number') {
        // Custom tag format
        x = tag.positionX;
        y = tag.positionY;
        z = tag.positionZ;
      } else {
        return false;
      }

      // Check if coordinates are valid numbers
      if (typeof x !== 'number' || typeof y !== 'number' || typeof z !== 'number') {
        return false;
      }

      const tagPosition = new THREE.Vector3(x, y, z);
      return frustum.containsPoint(tagPosition);
    });

    return visibleTags;
  } catch (err) {
    return [];
  }
}

/**
 * Gets all tags from the showcase
 */
export async function getAllTags(showcase: any) {
  try {
    if (!showcase?.Mattertag) {
      return [];
    }

    const tagData = await showcase.Mattertag.getData();
    return tagData;
  } catch (err) {
    return [];
  }
}

